import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api.js";
import { fmtDateTime } from "../lib/format.js";
import { AlertTriangle, ChevronLeft, Clock, ListChecks, Users } from "lucide-react";

const WINDOW_OPTIONS = [
  { label: "Últimas 24h", value: 24 },
  { label: "Últimas 48h", value: 48 },
  { label: "Últimos 7 dias", value: 168 },
];

/**
 * Relatório PT de "onboarding" pós-purge (iter 53).
 *
 * Mostra, por janela de tempo configurável, quantas SOMs foram abertas por
 * cada Service Advisor, o tempo médio até ao primeiro movimento, tarefas
 * pendentes vs. concluídas e alertas SLA (SOMs sem next_action ou com
 * deadline no passado). Usado pelo gestor para validar que o fluxo real
 * funciona antes de abrir o sistema a todos os utilizadores.
 */
export default function OnboardingReport() {
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async (h = hours) => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.get(`/reports/onboarding-24h?hours=${h}`);
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Falha ao carregar o relatório.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load(hours);
  }, [hours]); // load closes over hours; ignore stale-callback warning here

  return (
    <div className="max-w-[1400px] mx-auto p-4 sm:p-6" data-testid="onboarding-report">
      <div className="flex items-center gap-2 text-xs text-[#6C757D] mb-1">
        <Link to="/dashboard" className="flex items-center gap-1 hover:text-[#1F3864]">
          <ChevronLeft className="w-3 h-3" /> Painel
        </Link>
        <span>/</span>
        <span>Relatório de onboarding</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E]">
            Onboarding {hours}h
          </h1>
          <div className="text-xs text-[#6C757D] mt-0.5">
            Validação de fluxo com as primeiras obras reais.
            {data?.generated_at && (
              <> · Gerado {fmtDateTime(data.generated_at)}</>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {WINDOW_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => setHours(o.value)}
              className={`px-3 py-1.5 text-xs rounded-sm border ${
                hours === o.value
                  ? "bg-[#1F3864] text-white border-[#1F3864]"
                  : "bg-white text-[#495057] border-gray-300 hover:border-[#1F3864]"
              }`}
              data-testid={`onboarding-window-${o.value}`}
            >
              {o.label}
            </button>
          ))}
          <button
            onClick={() => load()}
            className="px-3 py-1.5 text-xs rounded-sm border border-gray-300 hover:border-[#1F3864] bg-white"
            data-testid="onboarding-refresh"
            disabled={busy}
          >
            {busy ? "A actualizar…" : "Actualizar"}
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-4 px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm">
          {err}
        </div>
      )}

      {data && (
        <>
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Kpi
              icon={<ListChecks className="w-4 h-4" />}
              label="SOMs abertas"
              value={data.totals.soms_opened}
              testid="kpi-soms-opened"
            />
            <Kpi
              icon={<Users className="w-4 h-4" />}
              label="Ainda em curso"
              value={data.totals.soms_open_now}
              testid="kpi-soms-open-now"
            />
            <Kpi
              icon={<Clock className="w-4 h-4" />}
              label="Tempo médio até 1º passo"
              value={
                data.totals.avg_time_to_first_move_min !== null
                  ? `${data.totals.avg_time_to_first_move_min} min`
                  : "—"
              }
              testid="kpi-avg-ttfm"
            />
            <Kpi
              icon={<AlertTriangle className="w-4 h-4" />}
              label="Tarefas pendentes"
              value={data.totals.tasks_pending}
              hint={`${data.totals.tasks_completed} concluídas · ${data.totals.tasks_created} criadas`}
              testid="kpi-tasks"
            />
          </section>

          <section className="mb-6">
            <h2 className="font-heading text-sm font-bold text-[#1A1A2E] mb-2">
              Por Service Advisor
            </h2>
            <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
                  <tr>
                    <th className="px-3 py-2 text-left">Advisor</th>
                    <th className="px-3 py-2 text-left">Filial</th>
                    <th className="px-3 py-2 text-right">Abertas</th>
                    <th className="px-3 py-2 text-right">Em curso</th>
                    <th className="px-3 py-2 text-right">Fechadas</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_advisor.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-3 py-6 text-center text-xs text-[#6C757D]">
                        Sem SOMs abertas nesta janela. Cria a primeira obra para começar a ver dados.
                      </td>
                    </tr>
                  ) : (
                    data.per_advisor.map((r, i) => (
                      <tr
                        key={r.advisor_id || `un-${i}`}
                        className="border-t border-gray-100"
                        data-testid={`onboarding-advisor-row-${i}`}
                      >
                        <td className="px-3 py-2">{r.advisor_name}</td>
                        <td className="px-3 py-2 text-xs text-[#6C757D]">{r.branch || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-[#1F3864]">
                          {r.opened}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{r.in_progress}</td>
                        <td className="px-3 py-2 text-right font-mono text-[#6C757D]">
                          {r.closed}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-sm font-bold text-[#1A1A2E] mb-2">
              Alertas SLA
            </h2>
            <div className="grid md:grid-cols-2 gap-3">
              <AlertCard
                title="SOMs sem próxima acção"
                items={data.sla_alerts.soms_missing_next_action}
                emptyText="Todas as SOMs têm próxima acção definida."
                testid="alerts-no-next-action"
              />
              <AlertCard
                title="SOMs com prazo ultrapassado"
                items={data.sla_alerts.soms_deadline_overdue}
                emptyText="Nenhum prazo ultrapassado."
                testid="alerts-deadline-overdue"
                showDeadline
              />
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Kpi({ icon, label, value, hint, testid }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#6C757D]">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold text-[#1A1A2E] mt-1 font-heading">{value}</div>
      {hint && <div className="text-[10px] text-[#6C757D] mt-0.5">{hint}</div>}
    </div>
  );
}

function AlertCard({ title, items, emptyText, testid, showDeadline }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-xs font-bold text-[#1A1A2E]">{title}</h3>
        <span
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${
            items.length ? "bg-[#F8D7DA] text-[#721C24]" : "bg-[#D4EDDA] text-[#155724]"
          }`}
        >
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-[#6C757D]">{emptyText}</div>
      ) : (
        <ul className="space-y-1">
          {items.slice(0, 8).map((it) => (
            <li key={it.id} className="text-xs flex items-center justify-between gap-2">
              <Link
                to={`/service-cases/${it.id}`}
                className="font-mono text-[#1F3864] hover:underline"
              >
                {it.number}
              </Link>
              {showDeadline && it.deadline && (
                <span className="text-[10px] text-[#6C757D]">
                  Prazo: {fmtDateTime(it.deadline)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {items.length > 8 && (
        <div className="text-[10px] text-[#6C757D] mt-1">
          + {items.length - 8} outros…
        </div>
      )}
    </div>
  );
}
